import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import momentTz from 'moment-timezone';
import React, { useState, useEffect, useMemo } from 'react';
import { momentLocalizer } from 'react-big-calendar';
import { useDispatch } from 'react-redux';

import EventCalendarComponent from './EventCalender';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'moment/locale/en-gb';

import { MessageTypeConstants } from '../../../../../../services/bot/Message';
import { getAuthData } from '../../../../../../services/storage/LocalStorage';
import CustomToolbar from '../../../../../common/CustomToolBar';
import { CalendarContainer } from '../../styles';
import { handleCalendarToolBarChange, setTabState } from '../../../../../../state/actions/tabStack';
import { processSendMessage } from '../../../../../../services/bot/conversation/SendMessage';
import { handleCalendarChange } from '../../../../../../state/actionsHelpers/tabStackActionsHelpers/message-type-container';
import { setBotWaitLoaderWithShimmer } from '../../../../../../state/actions/loader';

moment.locale('en');
const localizer = momentLocalizer(moment);

const CalendarView = ({
  conversation,
  options,
  message,
  messageType,
  parentTabId = null,
}) => {

  const dispatch = useDispatch();
  const { user: { userTimezone } } = getAuthData();
  const defaultUserTimezone = 'Etc/UTC';

  const [timeSVal, setTimeSVal] = useState('month');
  const [events, setEvents] = useState([]);
  const [someDay, setSomeDay] = useState(() => (options.startDate
    ? momentTz(options.startDate).tz(userTimezone || defaultUserTimezone)
    : momentTz().tz(userTimezone || defaultUserTimezone)));

  let selectedEvent = null;


  const renderDate = (type, data) => {
    if (type === 'end' && momentTz(data.value).tz(userTimezone || defaultUserTimezone).toDate().toString().includes('00:00:00')) {
      return momentTz(data.value)
        .tz(userTimezone || defaultUserTimezone)
        .endOf('day')
        .toDate();
    };

    return momentTz(data.value)
      .tz(userTimezone || defaultUserTimezone)
      .toDate();
  };


  useEffect(() => {
    if (message && !isEmpty(message) && options.calendarEntry) {
      const eventsList = [];
      if (isArray(message[0])) {
        // Old format data message
        message.forEach((item) => {
          const found = item?.find((i) => i.id === options.calendarEntry.id);
          const id = found?.value || '';
          const title = item?.find((i) => i.id === options.calendarEntry.title)?.value || '';
          const start = item?.find((i) => i.id === options.calendarEntry.start);
          const end = item?.find((i) => i.id === options.calendarEntry.end);
          const eventColor = item?.find((i) => i.type === 'color_field')?.value || '#638DFF';
          const content = options?.calendarEntry?.content?.map((elem) => {
            let obj = {};
            // eslint-disable-next-line no-shadow
            const found = item?.find((i) => i.id === elem);
            if (title !== found?.value) {
              obj = found;
              obj.id = elem;
            }
            return obj;
          });
          if (start?.value && end?.value) {
            eventsList.push({
              fields: item,
              content,
              id,
              title,
              start: start?.value ? renderDate('start', start) : null,
              end: end?.value ? renderDate('end', end) : null,
              eventColor,
              openPopper: false,
            });
          } else {
            console.error('Incorrect events passed', id, start, end, item);
          }
        });
      } else {
        // New format data message
        message.forEach((obj) => {
          const item = Object.values(obj)[0].fields;
          const found = item?.find((i) => i.id === options.calendarEntry.id);
          const id = found?.value || '';
          const title = item?.find((i) => i.id === options.calendarEntry.title)?.value || '';
          const start = item?.find((i) => i.id === options.calendarEntry.start);
          const end = item?.find((i) => i.id === options.calendarEntry.end);
          const eventColor = item?.find((i) => i.type === 'color_field')?.value || '#638DFF';
          const content = options?.calendarEntry?.content?.map((elem) => {
            // eslint-disable-next-line no-shadow
            let obj = {};
            // eslint-disable-next-line no-shadow
            const found = item?.find((i) => i.id === elem);
            if (title !== found?.value) {
              obj = found;
              obj.id = elem;
            }
            return obj;
          });
          if (start?.value && end?.value) {
            eventsList.push({
              fields: item,
              content,
              id,
              title,
              start: start?.value ? renderDate('start', start) : null,
              end: end?.value ? renderDate('end', end) : null,
              eventColor,
              openPopper: false,
            });
          } else {
            console.error('Incorrect events passed', id, start, end, item);
          }
        });
      }

      setEvents(eventsList);
    }
  }, [message]);

  useEffect(() => {
    if (events && events.length) {
      const eventsList = events?.map((event) => {
        const newEvent = event;
        if (selectedEvent === newEvent.id) {
          newEvent.openPopper = true;
        } else {
          newEvent.openPopper = false;
        }
        return newEvent;
      });

      setEvents(eventsList);
    }
  }, [selectedEvent]);


  const handleTimeBasedSearch = (someDayUnix, timeScaleVal, queryString) => {
    setTimeSVal(timeScaleVal);
    const mapper = { day: 2, week: 1, month: 0 };
    const responseChat = {};
    responseChat.messageType = MessageTypeConstants.MESSAGE_TYPE_TABLE_RESPONSE;
    responseChat.message = {
      controlId: options.controlId,
      tabId: options?.parent?.length ? parentTabId : options?.tabId,
      action: 'search',
      date: someDayUnix,
      timeScaleView: mapper[timeScaleVal],
      tz: userTimezone || defaultUserTimezone,
    };

    if (queryString !== void 0) {
      responseChat.message.queryString = queryString;
    }

    dispatch(setBotWaitLoaderWithShimmer(true));
    processSendMessage(responseChat);
  };


  const handleAction = (action = 'onAction', content = null) => {
    const message = {
      controlId: options.controlId,
      tabId: options.tabId,
      docId: options?.documentId,
      parentDocId: options?.parentDocId,
      action,
      content,
    };

    const responseChat = {
      messageType: MessageTypeConstants.MESSAGE_TYPE_TABLE_RESPONSE,
      message,
    };

    dispatch(setBotWaitLoaderWithShimmer(true));
    dispatch(setTabState(false));
    processSendMessage(responseChat);
  };


  const onNavigateDateChange = (date, view) => {
    const sDay = momentTz(date).tz(userTimezone || defaultUserTimezone);

    const sDayForUnix = sDay.clone();
    const sDayUnix = sDayForUnix.unix() * 1000;
    const sDayWeek = sDay.clone();
    const sDayWeekStart = sDayWeek.startOf('week');
    const sDayWeekStartForUnix = sDayWeekStart.clone();
    const sDayWeekStartUnix = sDayWeekStartForUnix.unix() * 1000;
    const sDayMonth = sDay.clone();
    const sDayMonthStart = sDayMonth.startOf('month');
    const sDayMonthStartForUnix = sDayMonthStart.clone();
    const sDayMonthStartUnix = sDayMonthStartForUnix.unix() * 1000;

    switch (view) {
      case 'day': {
        setSomeDay(sDay);
        handleTimeBasedSearch(sDayUnix, view);
        break;
      }
      case 'week': {
        setSomeDay(sDayWeekStart);
        handleTimeBasedSearch(sDayWeekStartUnix, view);
        break;
      }
      case 'month': {
        setSomeDay(sDayMonthStart);
        handleTimeBasedSearch(sDayMonthStartUnix, view);
        break;
      }
      default:
        break;
    }
  };

  const onViewChange = (value) => {
    switch (value) {
      case 'day': {
        const someDayUnix = someDay.unix() * 1000;
        handleTimeBasedSearch(someDayUnix, value);
        dispatch(handleCalendarChange(conversation, options, 'view', value));
        break;
      }
      case 'week': {
        const dayWeekStartUnix = someDay.startOf('week').unix() * 1000;
        handleTimeBasedSearch(dayWeekStartUnix, value);
        dispatch(handleCalendarChange(conversation, options, 'view', value));
        break;
      }
      case 'month': {
        const dayMonthStartUnix = someDay.startOf('month').unix() * 1000;
        handleTimeBasedSearch(dayMonthStartUnix, value);
        dispatch(handleCalendarChange(conversation, options, 'view', value));
        break;
      }
      default:
        break;
    }
  };

  const onSelectEvent = (event) => {
    const content = {};
    event.fields.map((item) => {
      content[item.id] = item.value;
      return content;
    });
    handleAction('onAction', content);
  };


  const { defaultDate, formats } = useMemo(() => ({
    defaultDate: options.startDate
      ? momentTz(options.startDate).tz(userTimezone || defaultUserTimezone).toDate()
      : momentTz().tz(userTimezone || defaultUserTimezone).toDate(),
    formats: {
      eventTimeRangeFormat: () => null,
      timeGutterFormat: (date, culture, localizer) => localizer.format(date, 'HH:mm', culture),
    },
  }), [options.startDate]);


  const changeSelectedEventRef = (id) => {
    selectedEvent = id;
  };

  const handleTimeBasedSearchCallback = (someDayUnix, currView, queryString) => {
    handleTimeBasedSearch(someDayUnix, currView || timeSVal, queryString);
  };

  const eventStyleGetter = ({ eventColor }) => ({ style: { backgroundColor: eventColor } });


  const currentView = options?.view || 'month';

  const calendarComponents = {
    toolbar: (props) => {
      dispatch(handleCalendarToolBarChange(props)); // Save calendar toolbar data to state
      return (
        <CustomToolbar
          {...props}
          options={options}
          messageType={messageType}
          conversation={conversation}
          handleAction={handleAction}
          doTimeBaseSearchCallback={handleTimeBasedSearchCallback}
        />
      );
    },
    event: (props) => (
      <EventCalendarComponent
        {...props}
        className="event-calendar-component"
        view={currentView}
        changeSelectedEventRef={changeSelectedEventRef}
      />
    ),
  };

  return (
    <CalendarContainer
      popup
      showMultiDayTimes
      events={events}
      startAccessor='start'
      endAccessor='end'
      views={['day', 'week', 'month']}
      style={{ height: '65vh' }}
      defaultDate={defaultDate}
      view={options?.view || 'month'}
      tooltipAccessor={null}
      formats={formats}
      onView={onViewChange}
      onSelectEvent={onSelectEvent}
      eventPropGetter={eventStyleGetter}
      onNavigate={onNavigateDateChange}
      components={calendarComponents}
      localizer={localizer}
    />
  );
};

export default React.memo(CalendarView);
